import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { ImportsMonitoringItem } from 'exports/interfaces/imports-monitoring-channel.interface';
import { ImportPartialSuccessStatusBarTooltipComponent } from 'shared/components/ag-grid/cell-renderers/import-partial-success-status-bar-renderer/import-partial-success-status-bar-tooltip/import-partial-success-status-bar-tooltip.component';
import { TooltipDirective } from 'shared/directives/tooltip.directive';

@Component({
    selector: 'app-import-partial-success-status-bar-renderer',
    imports: [CommonModule, TooltipDirective],
    templateUrl: './import-partial-success-status-bar-renderer.component.html',
    styleUrl: './import-partial-success-status-bar-renderer.component.scss',
})
export class ImportPartialSuccessStatusBarRendererComponent implements ICellRendererAngularComp {
    successPercentage = signal<null | number>(null);
    partialSuccessPercentage = signal<null | number>(null);
    totalItems = signal<null | number>(null);
    failedItems = signal<null | number>(null);
    partialSuccessItems = signal<null | number>(null);
    successItems = signal<null | number>(null);
    public data: ImportsMonitoringItem | null = null;
    tooltipComponent = ImportPartialSuccessStatusBarTooltipComponent;

    // Computed signals for adjusted percentages
    adjustedSuccessPercentage = computed(() => {
        const totalItemsValue = this.totalItems();
        const successItemsValue = this.successItems();
        const successPercentageValue = this.successPercentage();

        if (totalItemsValue && successItemsValue !== null && successItemsValue !== undefined) {
            return Math.round((successItemsValue / totalItemsValue) * 100);
        }
        return successPercentageValue ?? 0;
    });

    adjustedPartialSuccessPercentage = computed(() => {
        const totalItemsValue = this.totalItems();
        const partialSuccessItemsValue = this.partialSuccessItems();
        const partialSuccessPercentageValue = this.partialSuccessPercentage();

        if (totalItemsValue && partialSuccessItemsValue !== null && partialSuccessItemsValue !== undefined) {
            return Math.round((partialSuccessItemsValue / totalItemsValue) * 100);
        }
        return partialSuccessPercentageValue ?? 0;
    });

    adjustedFailurePercentage = computed(() => {
        const adjustedSuccessValue = this.adjustedSuccessPercentage();
        const adjustedPartialSuccessValue = this.adjustedPartialSuccessPercentage();
        const remaining = 100 - adjustedSuccessValue - adjustedPartialSuccessValue;

        return Math.max(0, remaining);
    });

    // Ensure percentages add up to exactly 100%
    normalizedSuccessPercentage = computed(() => {
        const success = this.adjustedSuccessPercentage();
        const partialSuccess = this.adjustedPartialSuccessPercentage();
        const failure = this.adjustedFailurePercentage();
        const total = success + partialSuccess + failure;

        if (total === 0) return 0;
        return Math.round((success / total) * 100 * 100) / 100; // Round to 2 decimal places
    });

    normalizedPartialSuccessPercentage = computed(() => {
        const success = this.adjustedSuccessPercentage();
        const partialSuccess = this.adjustedPartialSuccessPercentage();
        const failure = this.adjustedFailurePercentage();
        const total = success + partialSuccess + failure;

        if (total === 0) return 0;
        return Math.round((partialSuccess / total) * 100 * 100) / 100; // Round to 2 decimal places
    });

    normalizedFailurePercentage = computed(() => {
        const normalizedSuccess = this.normalizedSuccessPercentage();
        const normalizedPartialSuccess = this.normalizedPartialSuccessPercentage();
        return Math.max(0, 100 - normalizedSuccess - normalizedPartialSuccess);
    });

    agInit(params: ICellRendererParams): void {
        this.data = params.data as ImportsMonitoringItem;
        const importItem: ImportsMonitoringItem = params.data as ImportsMonitoringItem;
        this.successPercentage.set(importItem.successRate ?? null);
        this.partialSuccessPercentage.set(importItem.partialSuccessRate ?? null);
        this.totalItems.set(importItem.numberOfTotalItems ?? null);
        this.failedItems.set(importItem.numberOfFailedItems ?? null);
        this.partialSuccessItems.set(importItem.numberOfPartialSuccessfulItems ?? null);
        this.successItems.set(importItem.numberOfSuccessfulItems ?? null);

        // Set adjusted percentages on the data object
        if (this.data) {
            this.data.adjustedSuccessPercentage = this.adjustedSuccessPercentage();
            this.data.adjustedPartialSuccessPercentage = this.adjustedPartialSuccessPercentage();
            this.data.adjustedFailurePercentage = this.adjustedFailurePercentage();
        }
    }

    refresh(params: ICellRendererParams): boolean {
        return false;
    }
}
